// One-stop shell for the IAS deployment. Wraps children in the amelia SSO
// provider (owns the OIDC redirect dance), gates them behind the runtime
// settings load, and drops the session-expired modal in near the root so it
// renders above the app. Composed into upstream's `AppComponent` via a single
// hook line — nothing IAS-specific leaks into the upstream file beyond that.

import type { ReactNode } from "react";
import { Provider as ReactReduxProvider } from "react-redux";

import { useStore } from "metabase/redux";

import { AppSettingsGetter } from "./AppSettingsGetter";
import { AppSsoProvider } from "./AppSsoProvider";
import { PersistMetabaseUserInfo } from "./PersistMetabaseUserInfo";
import { SessionExpiredModal } from "./SessionExpiredModal";

export const IasAppShell = ({ children }: { children: ReactNode }) => {
  // Metabase wires only its custom `metabaseReduxContext`; the default
  // `ReactReduxContext` stays null. `@intelisysaviation/amelia-sso-client-react`
  // reads that default context internally, so we mount a second Provider with
  // the same store to give the SSO client — and anything else using the
  // default context — access without touching Metabase's own wiring.
  const store = useStore();
  return (
    <ReactReduxProvider store={store}>
      <PersistMetabaseUserInfo />
      <AppSsoProvider>
        {/*
          `SessionExpiredModal` lives OUTSIDE `AppSettingsGetter` so it can open
          regardless of the settings gate — including when the first settings
          load fails and the gate shows the generic error page instead.
        */}
        <SessionExpiredModal />
        <AppSettingsGetter>{children}</AppSettingsGetter>
      </AppSsoProvider>
    </ReactReduxProvider>
  );
};
